import PropTypes from 'prop-types';
import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';

import { addPageData, navigateWizard } from '../../actions';
import Button from '../../components/Button';
import CombinedTextField from '../../components/CombinedTextField';
import InputField from '../../components/InputField';
import PageLayout from '../../components/PageLayout';
import Select from '../../components/Select';
import analytics from '../../lib/analytics';
import { isNumber } from '../../lib/helpers';
import * as payloadFactory from '../../lib/pageDataPayloadFactory';
import { periodCurrencySelector } from '../../reducers/selectors';

export const PreviousProdigyLoanAmountPage = ({
  previousProdigyLoan,
  currency: loanCurrency,
  dispatchAddPageData,
  dispatchWizardNav,
}) => {
  const { amount: initialAmount = '', currency: initialCurrency = '' } = previousProdigyLoan || {};
  const currency = initialCurrency || loanCurrency;
  const currencies = [{ label: currency, value: currency }];
  const [amount, setAmount] = useState(initialAmount);

  const isInvalid = useMemo(() => {
    if (!isNumber(amount)) {
      return true;
    }

    const safeAmount = parseFloat(amount);
    if (safeAmount <= 0) {
      return true;
    }

    return false;
  }, [amount]);

  const onAmountChange = event => {
    event.preventDefault();

    setAmount(event.target.value);
  };

  const handleSubmit = event => {
    event.preventDefault();

    if (isInvalid) {
      return;
    }

    analytics.sendEvent('Click - Next', 'previous-prodigy-loan-amount');

    const payload = payloadFactory.createAddPageDataPayload('previousProdigyLoan', {
      currency,
      amount,
    });
    dispatchAddPageData(payload);
    dispatchWizardNav({ dir: 'fwd' });
  };

  const inputErrorClass = isInvalid ? 'has-error' : '';

  return (
    <PageLayout
      title="How much did you borrow for your previous loans?"
      helpText="We need to know how much you've already borrowed from us for tuition and living expenses.
                This is only for your previous study periods - don't worry about including any pre-approved amounts now."
    >
      <form className="row-spacer" onSubmit={handleSubmit}>
        <div className="row">
          <div className="col col--12/16-small col--10/16-medium">
            <CombinedTextField>
              <Select
                id="prodigy-loan-currency"
                value={currency}
                onChange={() => {}}
                options={currencies}
                disabled
              />

              <InputField
                type="number"
                id="prodigy-loan-amount"
                placeholder="Enter amount"
                value={amount}
                onChange={onAmountChange}
                className={inputErrorClass}
              />
            </CombinedTextField>
          </div>

          <div className="col col--auto">
            <Button type="submit" isDisabled={isInvalid}>
              Next
            </Button>
          </div>
        </div>
      </form>
    </PageLayout>
  );
};

PreviousProdigyLoanAmountPage.propTypes = {
  dispatchWizardNav: PropTypes.func.isRequired,
  dispatchAddPageData: PropTypes.func.isRequired,
  currency: PropTypes.string.isRequired,
  previousProdigyLoan: PropTypes.shape({
    currency: PropTypes.string.isRequired,
    amount: PropTypes.string.isRequired,
  }),
};

PreviousProdigyLoanAmountPage.defaultProps = {
  previousProdigyLoan: {
    currency: '',
    amount: '',
  },
};

export const mapStateToProps = state => ({
  previousProdigyLoan: state.builder.ui.previousProdigyLoan,
  currency: periodCurrencySelector(state),
});

export const mapDispatchToProps = {
  dispatchAddPageData: addPageData,
  dispatchWizardNav: navigateWizard,
};

export default connect(mapStateToProps, mapDispatchToProps)(PreviousProdigyLoanAmountPage);
